import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { UIModalComponent } from 'app/ui/ui-modal.component';
import { mentionEditorInit, mentionsIn } from 'app/shared/mention-editor';

/**
 * Create follow-up: add work to Needs Your Attention against any CRM record, from anywhere.
 *
 * Two questions in one dialog, in the order the design asks them — which record, then what. The
 * record comes first because a follow-up with nowhere to live is not a thing this model has: it
 * is always a note or a meeting *on* a record, and the widget only ever shows what those produce.
 *
 * Nothing here is a new kind of object. It writes an ordinary `crm_activity` through the same
 * save_activity the record's own panel uses, so a follow-up made here and one made on the deal
 * are the same row, and the widget lists them by the same rule.
 */
@Component({
	selector: 'app-core-attention-composer',
	templateUrl: './core-attention-composer.component.html',
	styleUrls: ['./core-attention-composer.component.scss']
})
export class CoreAttentionComposerComponent {

	@ViewChild('composer') composer: UIModalComponent;

	@Output() created = new EventEmitter<void>();

	// 'compose' is the form; 'picker' is the separate record chooser above it.
	step = 'compose';
	mode = 'note';

	record = null;
	title = '';
	body = '';
	date = '';
	time = '17:00';

	meeting = { duration_minutes: 60, method: 'on-site', location: '', attendees: '', agenda: '' };

	users: any[] = [];
	editorInit = null;
	saving = false;

	// The picker's own state, kept apart from the draft so opening it cannot disturb what has
	// been typed — the design is explicit that a draft survives going to the picker and back.
	search = '';
	results: any[] = [];
	searching = false;
	private searchTimer = null;

	readonly durations = [
		{ value: 30, label: '30 minutes' },
		{ value: 60, label: '1 hour' },
		{ value: 90, label: '1.5 hours' },
		{ value: 120, label: '2 hours' }
	];

	readonly methods = [
		{ value: 'on-site', label: 'On site' },
		{ value: 'video', label: 'Video call' },
		{ value: 'phone', label: 'Phone' }
	];

	constructor(
		public app: AppService,
		private api: ApiService
	) { }

	get canSave() {
		if (!this.record || !this.date || !this.time) return false;
		return this.mode === 'note' ? !!this.plainBody : !!this.title.trim();
	}

	// TinyMCE keeps its markup; what matters for "did they write anything" is the text.
	get plainBody() {
		return (this.body || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
	}

	get mentioned() {
		return mentionsIn(this.body);
	}

	open() {
		this.step = 'compose';
		this.mode = 'note';
		this.record = null;
		this.title = '';
		this.body = '';
		this.date = '';
		this.time = '17:00';
		this.meeting = { duration_minutes: 60, method: 'on-site', location: '', attendees: '', agenda: '' };
		this.search = '';
		this.results = [];

		// The same editor the record's own composer uses, so a mention written here is stored the
		// way every other mention is.
		this.editorInit = mentionEditorInit({
			users: () => this.users,
			placeholder: 'Write the note… type @ to tag a teammate',
			toolbar: 'bold italic underline | bullist numlist'
		});

		// The people who can be tagged. Fetched here rather than passed in: this component has no
		// host to hand it a list, and the widget it hangs off is global.
		this.api.crm.options(data => this.users = data?.users || [], () => { });

		this.composer.open();
	}

	close() {
		this.composer?.close();
	}

	// --- choosing the record -------------------------------------------------

	openPicker() {
		this.step = 'picker';
		if (!this.results.length) this.find();
	}

	// Typing searches, but not on every keystroke.
	onSearch() {
		clearTimeout(this.searchTimer);
		this.searchTimer = setTimeout(() => this.find(), 250);
	}

	/**
	 * Deals and leads together, which is what "a CRM record" means here.
	 *
	 * Two calls rather than one: they are two lists with two endpoints, both already applying the
	 * same visibility rule, and merging them here costs less than a third endpoint that would
	 * have to repeat it.
	 */
	find() {
		this.searching = true;
		const options = { search: this.search, page_size: 20, page_number: 1 };
		let pending = 2;
		let found = [];

		const done = () => {
			if (--pending) return;
			// Deals first: a follow-up is far more often about one, and the list is short.
			this.results = found.sort((a, b) => (a.type === b.type ? 0 : a.type === 'deal' ? -1 : 1));
			this.searching = false;
		};

		this.api.crm.dealList({ ...options, scope: 'open' }, data => {
			found = found.concat((data?.list || []).map(r => ({
				id: r.id, title: r.title, type: 'deal', ref: r.reference, stage: r.stage_name
			})));
			done();
		}, () => done());

		this.api.crm.list({ ...options, scope: 'inbox' }, data => {
			found = found.concat((data?.list || []).map(r => ({
				id: r.id, title: r.title, type: 'lead', ref: r.reference, stage: r.status
			})));
			done();
		}, () => done());
	}

	choose(row) {
		this.record = row;
		this.step = 'compose';
	}

	// --- saving --------------------------------------------------------------

	save() {
		if (!this.canSave || this.saving) return;

		this.saving = true;
		const due = this.date + ' ' + (this.time || '17:00') + ':00';

		/**
		 * An @mention is how it becomes somebody else's.
		 *
		 * The design's own words: add a mention to assign it to that person, and without one it
		 * stays in My attention. `assignee_id` is what the widget's "mine" rule reads first, so
		 * the first person tagged is the one it is handed to — the rest are still recorded as
		 * mentions and still see it, because being mentioned is one of the ways something is
		 * yours.
		 */
		const mentions = this.mentioned;

		this.api.crm.saveActivity({
			id: null,
			crm_id: this.record.id,
			activity_type: this.mode,
			title: this.title || null,
			body: this.mode === 'note' ? this.body : null,
			follow_up_datetime: due,
			assignee_id: mentions[0] || null,
			mentions,
			meeting: this.mode === 'meeting' ? { ...this.meeting, starts_at: due } : null,
			parent_id: null
		}, () => {
			this.saving = false;
			this.app.notifications.showSuccess('Follow-up created.',
				mentions.length ? 'It is now on their list.' : 'It is on your list.');
			this.close();
			this.created.emit();
		}, error => {
			this.saving = false;
			this.app.notifications.showDanger(error.message);
		});
	}

}
